import React from "react";
import { motion } from "framer-motion";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Feather, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { MascotContainer } from "@/components/site/primitives";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import Logo from "@/components/site/Logo";
import { useTheme } from "@/hooks/use-theme";
import { useAuth } from "@/lib/useAuth";
import { getPostAuthDestination } from "@/lib/authRedirect";
import { AuthDecor, AuthBackdrop } from "@/components/auth/AuthDecor";
import SignUpForm from "@/components/auth/SignUpForm";
import beeRead from "@/assets/bee-read.png";

const EASE = [0.22, 1, 0.36, 1];

export default function SignUp() {
  const { user, authStatus } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const isSuccess = location.pathname === "/signup/success";

  if (isSuccess && authStatus === "unauthenticated") return <Navigate to="/signup" replace />;
  if (user && authStatus === "authenticated" && !isSuccess) return <Navigate to={getPostAuthDestination({ from: location.state?.from })} replace />;

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-cream" data-testid="signup-page">
      <AuthBackdrop className="pointer-events-none absolute inset-0 -z-10" />
      <div className="relative z-20 flex items-center justify-between px-5 pt-5 md:px-10 md:pt-8">
        <Logo to="/" size="md" testId="signup-logo" />
        <ThemeToggle dark={dark} toggle={toggle} />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-[1240px] items-center gap-7 px-5 py-6 md:grid-cols-2 md:gap-8 md:px-10 md:py-10 lg:gap-16 lg:py-12">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="order-1 flex flex-col items-center text-center md:items-start md:text-left">
          <span className="u-eyebrow mb-3 md:mb-4"><span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> A corner for curious minds</span>
          <h1 className="max-w-lg font-display text-[clamp(2.2rem,5vw,4.2rem)] font-semibold leading-[1.02] tracking-tight text-ink">Make room for <span className="text-coral">better stories.</span></h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-subtle md:mt-5 md:text-base">Join a warm, lively place to keep your reading life close and your next idea closer.</p>

          <div className="mt-5 hidden w-full max-w-sm grid-cols-3 gap-2.5 md:grid md:mt-8">
            <div className="rounded-xl border border-line bg-card/65 p-3 shadow-soft"><BookOpen className="h-4 w-4 text-coral" /><p className="mt-2 text-xs font-semibold text-ink">Find your next read</p></div>
            <div className="rounded-xl border border-line bg-card/65 p-3 shadow-soft"><Feather className="h-4 w-4 text-blue" /><p className="mt-2 text-xs font-semibold text-ink">Write in public</p></div>
            <div className="rounded-xl border border-line bg-card/65 p-3 shadow-soft"><Sparkles className="h-4 w-4 text-yellow" /><p className="mt-2 text-xs font-semibold text-ink">Meet your people</p></div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.88, y: 22 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.15 }} className="relative mt-4 w-[34%] max-w-[150px] md:mt-8 md:w-[72%] md:max-w-[350px]">
            <MascotContainer src={beeRead} alt="ScribbleReads bee mascot reading a book" className="relative z-10 mx-auto w-full" />
            <AuthDecor className="pointer-events-none absolute inset-0 hidden md:block" />
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }} className="order-2 mx-auto w-full max-w-[440px] rounded-2xl border border-line bg-card/90 p-5 shadow-card backdrop-blur-xl sm:p-7">
          {isSuccess ? (
            <div className="py-8 text-center" role="status" data-testid="signup-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-sage" />
              <h2 className="mt-4 font-display text-2xl font-semibold text-ink">Your account is ready.</h2>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-subtle">Your reading corner is waiting. Start by finding a book you love.</p>
              <button
                type="button"
                onClick={() => navigate("/app/explore?welcome=1", { replace: true })}
                className="mt-7 inline-flex h-11 items-center justify-center gap-2 rounded-full bg-coral-fill px-5 text-sm font-semibold text-[#FFF7F0] shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
              >
                Find your first book <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="mb-5 text-center md:text-left"><h2 className="font-display text-2xl font-semibold text-ink">Create your account</h2><p className="mt-1.5 text-sm text-subtle">Your reading life, all in one place.</p></div>
              <SignUpForm />
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
